import type { EditorialApiDetail, EditorialEndpoint } from '../editorial';
import { endpointIdOf } from '../../lib/catalogPlatform';

/** Wraps sample data in the gateway's not-found envelope, pretty-printed. */
const envelope = (data: unknown): string =>
  JSON.stringify({ found: true, data }, null, 2);

interface ScEndpointInit extends Omit<EditorialEndpoint, 'id' | 'action' | 'sku' | 'inputExample' | 'sampleResponse'> {
  action?: string;
  input: Record<string, unknown>;
  sample: unknown;
}

interface ScApiInit extends Omit<EditorialApiDetail, 'endpoints'> {
  skuPrefix?: string;
  endpoints: Record<string, ScEndpointInit>;
}

const endpointAction = (id: string): string =>
  id.replaceAll(/[-_]/g, ' ').replace(/^./, (initial) => initial.toUpperCase());

/** Assembles editorial content while deriving each discovery SKU once.
 *  The route id comes from `endpointIdOf` so authored endpoints share one URL
 *  vocabulary with the live-only ones: `linkedin.search_posts` is always
 *  `/api/linkedin/search-posts`, never the underscored key. */
export const scApi = ({
  id, name, category, color, blurb, skuPrefix = id, endpoints,
}: ScApiInit): EditorialApiDetail => ({
  id,
  name,
  category,
  color,
  blurb,
  endpoints: Object.entries(endpoints).map(([endpointId, init]) => {
    const sku = `${skuPrefix}.${endpointId.replaceAll('-', '_')}`;
    return {
      id: endpointIdOf(sku),
      action: init.action ?? endpointAction(endpointId),
      description: init.description,
      sku,
      inputExample: JSON.stringify(init.input, null, 2),
      sampleResponse: envelope(init.sample),
    };
  }),
});

/** Social catalog defaults shared by every supported social platform. */
export const socialApi = (
  { id, name, blurb, skuPrefix, endpoints }: Omit<ScApiInit, 'category' | 'color'>,
): EditorialApiDetail => scApi({
  id, name, category: 'Social', color: 'var(--c-violet)', blurb, skuPrefix, endpoints,
});
